import { useState } from 'react';
import { supabase } from '../../../lib/supabase';

const SKILLS = [
  { icon: 'ri-reactjs-line', label: 'React / Vue' },
  { icon: 'ri-database-2-line', label: '後端開發' },
  { icon: 'ri-smartphone-line', label: 'App 開發' },
  { icon: 'ri-robot-2-line', label: 'AI / ML' },
  { icon: 'ri-shield-check-line', label: '資安' },
  { icon: 'ri-layout-3-line', label: 'UI / UX' },
];

export default function JoinUsSection() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    const form = e.currentTarget;
    const formData: Record<string, string> = {};
    const urlData = new URLSearchParams();
    const elements = form.elements as HTMLFormControlsCollection;
    Array.from(elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (input.name && input.type !== 'checkbox') {
        formData[input.name] = input.value;
        urlData.append(input.name, input.value);
      }
    });
    setSubmitting(true);
    try {
      await Promise.all([
        fetch('https://readdy.ai/api/form/d6ugm0m7ipaugi04l980', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: urlData.toString(),
        }),
        supabase.from('talent_applications').insert({
          name: formData['name'] || '',
          email: formData['email'] || '',
          skill: formData['skill'] || '',
          intro: formData['intro'] || '',
        }),
      ]);
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="relative py-20 md:py-28 overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f2a1e 100%)' }}>
      {/* 背景裝飾光暈 */}
      <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(13,148,136,0.12) 0%, transparent 70%)' }}></div>
      <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.08) 0%, transparent 70%)' }}></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-20">

          {/* ── 左側說明 ── */}
          <div className="flex-1 w-full">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-bold mb-6" style={{ background: 'rgba(251,191,36,0.12)', borderColor: 'rgba(251,191,36,0.3)', color: '#fcd34d' }}>
              <i className="ri-team-line"></i>
              加入我們的團隊
            </div>

            <h2 className="text-3xl md:text-5xl font-black text-white mb-3 leading-tight">
              我們正在尋找<br />
              <span style={{ color: '#34d399' }}>志同道合的</span><br />
              技術夥伴
            </h2>

            {/* 地點標籤 */}
            <div className="flex items-center gap-2 mb-6">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold" style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.75)' }}>
                <i className="ri-map-pin-2-line text-sm" style={{ color: '#fcd34d' }}></i>
                台灣 · 台南市
              </div>
            </div>

            <p className="text-sm md:text-base mb-8 leading-relaxed" style={{ color: 'rgba(255,255,255,0.65)' }}>
              藍舍 AI 地產目前處於開放測試階段，若您對房地產科技有熱情，同時具備資訊工程相關背景，我們非常歡迎您加入，一起打造台灣最好的零仲介費直租平台。
            </p>

            {/* 技能標籤 */}
            <div className="mb-8">
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'rgba(255,255,255,0.35)' }}>歡迎的技術背景</p>
              <div className="flex flex-wrap gap-2">
                {SKILLS.map((s) => (
                  <div key={s.label} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold" style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)' }}>
                    <i className={`${s.icon} text-sm`} style={{ color: '#34d399' }}></i>
                    {s.label}
                  </div>
                ))}
              </div>
            </div>

            {/* 我們提供的 */}
            <div className="space-y-3">
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'rgba(255,255,255,0.35)' }}>我們能提供的</p>
              {[
                { icon: 'ri-rocket-2-line', title: '真實產品參與', desc: '直接參與一個已上線、有真實用戶的產品開發' },
                { icon: 'ri-sparkling-2-line', title: 'AI 技術實踐', desc: '深度接觸 Gemini AI、Supabase、Edge Functions 等現代技術棧' },
                { icon: 'ri-handshake-line', title: '彈性合作模式', desc: '兼職、遠端皆可，歡迎討論各種合作方式' },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3 p-3.5 rounded-2xl border" style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.08)' }}>
                  <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-xl" style={{ background: 'rgba(52,211,153,0.15)' }}>
                    <i className={`${item.icon} text-sm`} style={{ color: '#34d399' }}></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white">{item.title}</p>
                    <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.5)' }}>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── 右側表單 ── */}
          <div className="w-full lg:w-[420px] flex-shrink-0">
            <div className="rounded-3xl overflow-hidden border" style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.1)' }}>

              <div className="px-6 py-5 border-b" style={{ background: 'rgba(52,211,153,0.08)', borderColor: 'rgba(255,255,255,0.08)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl" style={{ background: 'rgba(52,211,153,0.2)' }}>
                    <i className="ri-mail-send-line text-base" style={{ color: '#34d399' }}></i>
                  </div>
                  <div>
                    <p className="text-white font-black text-sm">聯繫我們</p>
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>填寫後我們會盡快回覆您</p>
                  </div>
                </div>
              </div>

              {submitted ? (
                <div className="px-6 py-12 flex flex-col items-center text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full mb-4" style={{ background: 'rgba(52,211,153,0.15)' }}>
                    <i className="ri-checkbox-circle-line text-3xl" style={{ color: '#34d399' }}></i>
                  </div>
                  <p className="text-white font-black text-lg mb-1">收到了！</p>
                  <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>感謝您的興趣，我們會盡快與您聯繫。</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form className="px-6 py-6 space-y-4">

                  <div>
                    <label className="block text-xs font-bold mb-1.5" style={{ color: 'rgba(255,255,255,0.7)' }}>姓名 <span style={{ color: '#34d399' }}>*</span></label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="您的名字"
                      className="w-full px-3.5 py-2.5 text-sm rounded-xl border focus:outline-none transition-colors"
                      style={{ background: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.12)', color: 'white' }}
                      onFocus={e => (e.currentTarget.style.borderColor = 'rgba(52,211,153,0.6)')}
                      onBlur={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)')}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold mb-1.5" style={{ color: 'rgba(255,255,255,0.7)' }}>Email <span style={{ color: '#34d399' }}>*</span></label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      className="w-full px-3.5 py-2.5 text-sm rounded-xl border focus:outline-none transition-colors"
                      style={{ background: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.12)', color: 'white' }}
                      onFocus={e => (e.currentTarget.style.borderColor = 'rgba(52,211,153,0.6)')}
                      onBlur={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)')}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold mb-1.5" style={{ color: 'rgba(255,255,255,0.7)' }}>技術背景 <span style={{ color: '#34d399' }}>*</span></label>
                    <select
                      name="skill"
                      required
                      className="w-full px-3.5 py-2.5 text-sm rounded-xl border focus:outline-none cursor-pointer"
                      style={{ background: '#1e293b', borderColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)' }}
                    >
                      <option value="">請選擇最符合的方向</option>
                      <option value="前端開發 React/Vue">前端開發（React / Vue）</option>
                      <option value="後端開發">後端開發</option>
                      <option value="全端開發">全端開發</option>
                      <option value="App 開發">App 開發（iOS / Android）</option>
                      <option value="AI / 機器學習">AI / 機器學習</option>
                      <option value="資安">資訊安全</option>
                      <option value="UI/UX 設計">UI / UX 設計</option>
                      <option value="其他資訊背景">其他資訊相關背景</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold mb-1.5" style={{ color: 'rgba(255,255,255,0.7)' }}>簡短自我介紹 <span style={{ color: '#34d399' }}>*</span></label>
                    <textarea
                      name="intro"
                      required
                      placeholder="簡單介紹您的經驗、對這個項目的興趣，或任何想說的話..."
                      maxLength={500}
                      rows={4}
                      onChange={e => setCharCount(e.target.value.length)}
                      className="w-full px-3.5 py-2.5 text-sm rounded-xl border focus:outline-none resize-none"
                      style={{ background: 'rgba(255,255,255,0.07)', borderColor: 'rgba(255,255,255,0.12)', color: 'white' }}
                      onFocus={e => (e.currentTarget.style.borderColor = 'rgba(52,211,153,0.6)')}
                      onBlur={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)')}
                    />
                    <p className={`text-right text-[10px] mt-0.5 ${charCount > 500 ? 'text-red-400' : ''}`} style={{ color: charCount > 500 ? '#f87171' : 'rgba(255,255,255,0.3)' }}>
                      {charCount} / 500
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting || charCount > 500}
                    className="w-full flex items-center justify-center gap-2 py-3 text-sm font-black rounded-xl cursor-pointer whitespace-nowrap transition-opacity disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg, #0d9488, #34d399)', color: '#0f172a' }}
                  >
                    {submitting ? (
                      <><i className="ri-loader-4-line animate-spin text-sm"></i>送出中...</>
                    ) : (
                      <><i className="ri-send-plane-fill text-sm"></i>送出申請</>
                    )}
                  </button>

                  <p className="text-center text-[11px]" style={{ color: 'rgba(255,255,255,0.3)' }}>
                    我們通常在 1～3 個工作天內回覆
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}